import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Typography,
    Box,
    TextField,
    Autocomplete,
    CircularProgress,
} from '@mui/material';

import { GroundStationManager, ScheduleManager } from 'models';

import { useAppDispatch, useAppSelector } from 'hooks';
import { sendGroundStationCommand } from 'state/groundstations';
import { getScheduleSummeryList } from 'state/schedules';
import { receiveMessage } from 'state/websockets';

import StepPlan from 'components/schedules/StepPlan';
import { toReadableTimeFromNow } from 'utils';

interface Props {
    groundstation: GroundStationManager.GroundStation;
}

const GetStepPlan: React.FunctionComponent<Props> = ({ groundstation }) => {
    const dispatch = useAppDispatch();

    const { latestCommands } = useAppSelector((root) => root.groundstations);

    const [open, setOpen] = React.useState(false);
    const [scheduleOptions, setScheduleOptions] = React.useState<
        ScheduleManager.ScheduleSummary[]
    >([]);
    const loading = useMemo(
        () => open && scheduleOptions.length === 0,
        [open, scheduleOptions],
    );

    const [schedule, setSchedule] =
        useState<ScheduleManager.ScheduleSummary | null>(null);

    const [steps, setSteps] = useState<ScheduleManager.Step[]>();

    const [sentCommand, setSentCommand] =
        useState<GroundStationManager.API.Command>();

    const [sending, setSending] = useState(false);

    const getSteps = useCallback(async () => {
        if (!groundstation.connectionId || !schedule) return;

        setSending(true);

        try {
            const results = await dispatch(
                sendGroundStationCommand({
                    id: groundstation.id,
                    command: 'get_plan',
                    params: {
                        schedule_id: schedule.id,
                    },
                }),
            );

            if (results.error) {
                dispatch(
                    receiveMessage({
                        command: 'message',
                        is_ground_station: true,
                        from_id: groundstation.id,
                        from: groundstation.name,
                        message:
                            results.error ??
                            'No Error Message from Ground Station',
                        status: 'error',
                    }),
                );
                setSending(false);
            }

            if (results.command) setSentCommand(results.command);
        } catch (err) {
            setSending(false);
        }
    }, [schedule, groundstation]);

    useEffect(() => {
        if (schedule) getSteps();
    }, [schedule]);

    useEffect(() => {
        const latestSentCommand =
            sentCommand != null ? latestCommands[sentCommand.id] : null;

        if (latestSentCommand && groundstation) {
            if (latestSentCommand.status === 'FINISHED') {
                setSteps(
                    (latestSentCommand.results?.steps ??
                        []) as ScheduleManager.Step[],
                );
                setSentCommand(undefined);
            } else if (latestSentCommand.status === 'ERROR') {
                dispatch(
                    receiveMessage({
                        command: 'message',
                        is_ground_station: true,
                        from_id: groundstation.id,
                        from: groundstation.name,
                        message:
                            latestSentCommand.error ??
                            'No Error Message from Ground Station',
                        status: 'error',
                    }),
                );
            }
            setSending(false);
        }
    }, [latestCommands]);

    useEffect(() => {
        if (!loading) return undefined;

        dispatch(
            getScheduleSummeryList({
                after_stop_time: new Date(
                    Date.now() - 2 * 24 * 60 * 60 * 1000,
                ).toISOString(),
                antenna__groundstation: groundstation.id,
                ordering: 'start_time',
                limit: 250,
            }),
        ).then((summeries) => {
            setScheduleOptions(summeries);
        });
    }, [loading]);

    useEffect(() => {
        if (!open) setScheduleOptions([]);
    }, [open]);

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateRows: 'auto 1fr',
                gap: 1,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                }}
            >
                <Box>
                    <Typography variant="h6">Schedule Step Plan</Typography>
                    <Typography variant="subtitle1">
                        Displays the Step Plan of a Schedule
                    </Typography>
                </Box>
            </Box>
            <Box>
                <Autocomplete<ScheduleManager.ScheduleSummary>
                    value={schedule}
                    open={open}
                    noOptionsText="No Schedules Found"
                    fullWidth
                    onOpen={() => {
                        setOpen(true);
                    }}
                    onClose={() => {
                        setOpen(false);
                    }}
                    onChange={(
                        event: any,
                        newValue: ScheduleManager.ScheduleSummary | null,
                    ) => {
                        if (!newValue || !schedule) {
                            // Set to None or if Schedule is current null (nothing loadded) then set it
                            setSchedule(newValue);
                            return;
                        }

                        if (schedule && newValue.id !== schedule.id) {
                            // Only 'change' if the objects change id. It's possible
                            // for a change to occur but the Ids stay the same.
                            // I want to avoid spamming this more expensive operation
                            setSchedule(newValue);
                            setSteps(undefined);
                        }
                    }}
                    isOptionEqualToValue={(
                        option: ScheduleManager.ScheduleSummary,
                        value: ScheduleManager.ScheduleSummary,
                    ) => option.id === value.id}
                    getOptionLabel={(option: ScheduleManager.ScheduleSummary) =>
                        `#${option.id} ${
                            option.satelliteName
                        }: ${option.start_time.toLocaleTimeString()} - ${option.stop_time.toLocaleTimeString()} ${option.stop_time.toLocaleDateString()} (${toReadableTimeFromNow(
                            option.start_time,
                        )})`
                    }
                    options={scheduleOptions}
                    loading={loading}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Select a Schedule"
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <React.Fragment>
                                        {loading ? (
                                            <CircularProgress
                                                color="inherit"
                                                size={20}
                                            />
                                        ) : null}
                                        {params.InputProps.endAdornment}
                                    </React.Fragment>
                                ),
                            }}
                        />
                    )}
                />
            </Box>
            <Box
                sx={{
                    display: 'grid',
                    gap: 1,
                }}
            >
                {!sending && (!steps || steps.length == 0) && (
                    <Typography variant="subtitle2">
                        No Steps Available
                    </Typography>
                )}
                {!groundstation.connectionId && (
                    <Typography variant="subtitle2">
                        Ground Station is not Connected
                    </Typography>
                )}
                {sending && (
                    <Box
                        sx={{
                            width: '100%',
                            display: 'grid',
                            placeItems: 'center',
                        }}
                    >
                        <CircularProgress size={20} />
                    </Box>
                )}
                {steps && schedule && steps.length > 0 && (
                    <StepPlan scheduleId={schedule.id} steps={steps} />
                )}
            </Box>
        </Box>
    );
};

GetStepPlan.displayName = 'GetStepPlan';
export default GetStepPlan;
